'use client';

// Third Party
import React, {
  ComponentType,
  forwardRef,
  useImperativeHandle,
  useRef,
} from 'react';
import type { CustomArrowProps, LazyLoadTypes, Settings } from 'react-slick';
import Slider from 'react-slick';

// UI
import { Icon, IconNames } from '@components/icon';

interface CarouselProps {
  children: React.ReactNode;
  showDots?: boolean;
  showArrows?: boolean;
  className?: string;
  nextArrow?: React.ComponentType<CustomArrowProps>;
  prevArrow?: React.ComponentType<CustomArrowProps>;
  responsive?: Settings['responsive'];
  onSlideChange?: (index: number) => void;
  loadingStrategy?: LazyLoadTypes;
  useAspectRatio?: boolean;
  // Value section specific props
  autoHeight?: boolean;
  brandColorDots?: boolean;
  centerPadding?: string;
  disableSwipe?: boolean;
}

const NextArrow = (props: CustomArrowProps) => {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="absolute right-2 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 p-2 hover:bg-white"
    >
      <Icon name={IconNames.CHEVRON_RIGHT} className="h-4 w-4" />
    </button>
  );
};

const PrevArrow = (props: CustomArrowProps) => {
  const { onClick } = props;
  return (
    <button
      onClick={onClick}
      className="absolute left-2 top-1/2 z-10 flex h-7 w-7 -translate-y-1/2 items-center justify-center rounded-full bg-white/80 p-2 hover:bg-white"
    >
      <Icon name={IconNames.CHEVRON_LEFT} className="h-4 w-4" />
    </button>
  );
};

// Cast Slider to ComponentType to resolve React JSX type conflicts
const SliderComponent = Slider as ComponentType<
  Settings & { ref?: React.Ref<Slider> }
>;

interface CarouselRef {
  slickGoTo: (slide: number) => void;
  slickNext: () => void;
  slickPrev: () => void;
}

export const Carousel = forwardRef<CarouselRef, CarouselProps>(
  (
    {
      children,
      showDots = true,
      showArrows = true,
      className,
      nextArrow,
      prevArrow,
      responsive,
      onSlideChange,
      loadingStrategy = 'ondemand',
      useAspectRatio = true,
      autoHeight = false,
      brandColorDots = false,
      centerPadding = '20px',
      disableSwipe = false,
    },
    ref
  ) => {
    const sliderRef = useRef<Slider>(null);

    useImperativeHandle(ref, () => ({
      slickGoTo: (slide: number) => {
        sliderRef.current?.slickGoTo(slide);
      },
      slickNext: () => {
        sliderRef.current?.slickNext();
      },
      slickPrev: () => {
        sliderRef.current?.slickPrev();
      },
    }));

    const settings: Settings = {
      dots: showDots,
      infinite: true,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: showArrows,
      nextArrow: nextArrow ? React.createElement(nextArrow) : <NextArrow />,
      prevArrow: prevArrow ? React.createElement(prevArrow) : <PrevArrow />,
      adaptiveHeight: autoHeight,
      lazyLoad: loadingStrategy,
      dotsClass: 'slick-dots custom-dots',
      centerPadding: centerPadding,
      swipe: !disableSwipe,
      touchMove: !disableSwipe,
      draggable: !disableSwipe,
      responsive,
      beforeChange: (_oldIndex: number, newIndex: number) => {
        onSlideChange?.(newIndex);
      },
    };

    const wrapperClasses = [
      'carousel-wrapper',
      // Only apply aspect-ratio if autoHeight is false
      useAspectRatio && !autoHeight ? 'aspect-ratio' : '',
      autoHeight ? 'auto-height' : '',
      brandColorDots ? 'brand-color-dots' : '',
      className || '',
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClasses}>
        <SliderComponent ref={sliderRef} {...settings}>
          {/* @ts-expect-error — react-slick types use older @types/react that conflicts with React 19 ReactNode */}
          {children}
        </SliderComponent>
      </div>
    );
  }
);

Carousel.displayName = 'ValueSectionCarousel';
